
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuthSession } from '@/hooks/useAuthSession';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useIsMobile } from '@/hooks/use-mobile';

const feedbackFormSchema = z.object({
  feedbackText: z.string().min(10, {
    message: 'Feedback must be at least 10 characters.',
  }),
  category: z.string().min(1, {
    message: 'Please select a category.',
  }),
});

type FeedbackFormValues = z.infer<typeof feedbackFormSchema>;

const Feedback = () => {
  const { userId } = useAuthSession();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { isMobile, isSmallMobile } = useIsMobile();

  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackFormSchema),
    defaultValues: {
      feedbackText: '',
      category: '',
    },
  });

  const onSubmit = async (data: FeedbackFormValues) => {
    if (!userId) {
      toast.error('You need to be logged in to submit feedback.');
      return;
    }

    try {
      setIsSubmitting(true);
      
      const { error } = await supabase
        .from('user_feedback')
        .insert({
          user_id: userId,
          feedback_text: data.feedbackText,
          category: data.category,
        });

      if (error) throw error;

      toast.success('Your feedback has been submitted. Thank you!');
      form.reset();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`max-w-2xl mx-auto ${isMobile ? 'p-3 pb-20' : 'p-6'}`}>
      <div className="mb-6 space-y-2">
        <h1 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold`}>Submit Feedback</h1>
        <p className={`${isSmallMobile ? 'text-sm' : ''} text-muted-foreground`}>
          We value your feedback! Let us know how we can improve our platform.
        </p>
      </div>

      <div className={`bg-card rounded-lg border ${isMobile ? 'p-3' : 'p-6'} shadow-sm`}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="feature">Feature Request</SelectItem>
                      <SelectItem value="bug">Bug Report</SelectItem>
                      <SelectItem value="content">Content Feedback</SelectItem>
                      <SelectItem value="usability">Usability Issue</SelectItem>
                      <SelectItem value="performance">Performance Issue</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="feedbackText"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Feedback</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Please describe your feedback or suggestion in detail..."
                      className={`${isMobile ? 'min-h-[120px]' : 'min-h-[150px]'} resize-y`}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-primary to-secondary"
              disabled={isSubmitting}
              size={isSmallMobile ? "sm" : "default"}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default Feedback;
